import { makePersisted } from "@solid-primitives/storage"
import posthog from "posthog-js"
import {
  createContext,
  createSignal,
  onMount,
  type ParentProps,
  useContext,
} from "solid-js"

const FEATURE_FLAG_KEYS = ["placeholder-feature-flag"] as const

type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number]

export type FeatureFlags = Record<FeatureFlagKey, boolean>

const IS_DEV = import.meta.env.VITE_ENV === "development"

function createDefaultFlags(defaultValue: boolean): FeatureFlags {
  const flags: Partial<FeatureFlags> = {}
  for (const key of FEATURE_FLAG_KEYS) {
    flags[key] = defaultValue
  }
  return flags as FeatureFlags
}

function fetchFlagsFromPosthog(): Partial<FeatureFlags> {
  if (IS_DEV) return {}

  const flags: Partial<FeatureFlags> = {}
  for (const key of FEATURE_FLAG_KEYS) {
    flags[key] = posthog.isFeatureEnabled(key) ?? PRODUCTION_DEFAULTS[key]
  }
  return flags
}

const PRODUCTION_DEFAULTS = createDefaultFlags(false)
const DEV_DEFAULTS = createDefaultFlags(true)

const OVERRIDE_KEY = "fika-feature-flags-override"

interface FeatureFlagOverride {
  mode: "all-on" | "all-off" | "custom" | null
  custom?: Partial<FeatureFlags>
}

interface FeatureFlagsContextValue {
  flags: () => FeatureFlags
  isLoading: () => boolean
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>()

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext)

  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider")
  }

  return context
}

function getStoredOverride(): FeatureFlagOverride | null {
  try {
    const stored = localStorage.getItem(OVERRIDE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

function setStoredOverride(override: FeatureFlagOverride | null): void {
  if (override === null) {
    localStorage.removeItem(OVERRIDE_KEY)
  } else {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(override))
  }
}

function applyOverrides(baseFlags: FeatureFlags): FeatureFlags {
  if (!IS_DEV) return baseFlags

  const override = getStoredOverride()
  if (!override) return baseFlags

  if (override.mode === "all-on") {
    return createDefaultFlags(true)
  }

  if (override.mode === "all-off") {
    return createDefaultFlags(false)
  }

  if (override.mode === "custom" && override.custom) {
    return { ...baseFlags, ...override.custom }
  }

  return baseFlags
}

export function FeatureFlagsProvider(props: ParentProps) {
  const [flags, setFlags] = makePersisted(
    createSignal<FeatureFlags>(IS_DEV ? DEV_DEFAULTS : PRODUCTION_DEFAULTS),
    {
      name: "fika-feature-flags",
      storage: localStorage,
    },
  )
  const [isLoading, setIsLoading] = createSignal(true)

  function updateFlags(newFlags: FeatureFlags | Partial<FeatureFlags>): void {
    const completeFlags: FeatureFlags = { ...PRODUCTION_DEFAULTS, ...newFlags }
    const finalFlags = applyOverrides(completeFlags)
    setFlags(finalFlags)
  }

  async function fetchFlags(): Promise<void> {
    if (IS_DEV || !posthog.__loaded) return

    await posthog.reloadFeatureFlags()
    const newFlags = fetchFlagsFromPosthog()
    updateFlags(newFlags)
  }

  function setupDevHelpers(): void {
    const helpers = {
      enableFlag(flag: FeatureFlagKey): void {
        const override = getStoredOverride() || {
          mode: "custom" as const,
          custom: {},
        }
        override.mode = "custom"
        override.custom = { ...override.custom, [flag]: true }
        setStoredOverride(override)
        updateFlags(flags())
        console.log(`✓ enabled flag: ${flag}`)
      },

      disableFlag(flag: FeatureFlagKey): void {
        const override = getStoredOverride() || {
          mode: "custom" as const,
          custom: {},
        }
        override.mode = "custom"
        override.custom = { ...override.custom, [flag]: false }
        setStoredOverride(override)
        updateFlags(flags())
        console.log(`✗ disabled flag: ${flag}`)
      },

      enableAll(): void {
        setStoredOverride({ mode: "all-on" })
        updateFlags(flags())
        console.log("✓ enabled all feature flags")
      },

      disableAll(): void {
        setStoredOverride({ mode: "all-off" })
        updateFlags(flags())
        console.log("✗ disabled all feature flags")
      },

      reset(): void {
        setStoredOverride(null)
        updateFlags(IS_DEV ? DEV_DEFAULTS : flags())
        console.log("↻ reset to defaults")
      },

      status(): void {
        const current = flags()
        const override = getStoredOverride()
        console.log("📋 feature flags status:")
        console.table(current)
        if (override) {
          console.log("override mode:", override.mode)
          if (override.custom) {
            console.log("custom overrides:", override.custom)
          }
        }
      },

      help(): void {
        console.log(`
🎌 feature flags helpers (development mode)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

featureFlags.status()              - show current flag status
featureFlags.enableAll()           - enable all flags
featureFlags.disableAll()          - disable all flags
featureFlags.enableFlag('name')    - enable specific flag
featureFlags.disableFlag('name')   - disable specific flag
featureFlags.reset()               - reset to defaults

available flags: ${FEATURE_FLAG_KEYS.join(", ")}
        `)
      },
    }

    ;(window as any).featureFlags = helpers
    helpers.status()
  }

  onMount(() => {
    if (IS_DEV) {
      updateFlags(IS_DEV ? DEV_DEFAULTS : PRODUCTION_DEFAULTS)
      setupDevHelpers()
      setIsLoading(false)
      return
    }

    posthog.onFeatureFlags(() => {
      const newFlags = fetchFlagsFromPosthog()
      updateFlags(newFlags)
      setIsLoading(false)
    })

    if (posthog.__loaded) {
      fetchFlags().then(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  })

  const contextValue: FeatureFlagsContextValue = {
    flags,
    isLoading,
  }

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {props.children}
    </FeatureFlagsContext.Provider>
  )
}
